import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Tabs } from "@/components/ui/Tabs/Tabs";
import { Segmented } from "@/components/ui/Segmented/Segmented";
import { Modal } from "@/components/ui/Modal/Modal";
import { Input } from "@/components/ui/Input/Input";
import { Checkbox } from "@/components/ui/Checkbox/Checkbox";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useWorkspace,
  useTrendConfig,
  useUpdateTrendConfig,
  useConnectDigestSlack,
  useDisconnectDigestSlack,
  useTrendSources,
  useConnectTrendSource,
  useDisconnectTrendSource,
  useTrendClusters,
  useTrendCluster,
  usePushClusterToBrain,
  useGenerateFromCluster,
  useRescanTrends,
  useTrendScanJob,
  useTrendDigests,
  useMarkDigestRead,
  type TrendClusterRecord,
  type TrendConfigRecord,
  type TrendDigestRecord,
  type TrendSourceAccountRecord,
  type TrendSourceConfig,
  type TrendSourceKind,
  type TrendTopicRecord,
  type DraftProvider,
} from "@/lib/queries";
import type { TrendContribution } from "@notamen/core/db";
import { PLATFORMS, type Platform, type ContentKind } from "@notamen/core/types";
import styles from "./Trends.module.css";

/**
 * Trends — market-trends scanner surface.
 *
 * Wired to the live trends API (clusters, sources, config, scan jobs,
 * digests) through the TanStack Query hooks in
 * `apps/web/src/lib/queries.ts`. One tile remains an explicit scaffold
 * because it has no backing data yet (the hero "Quota used" tile); it
 * carries a SCAFFOLD comment naming what it awaits.
 *
 * Design source of truth: docs/design-mockups/trends.jsx + DESIGN_SPEC §4.34.
 */

type TabKey = "trends" | "sources" | "hooks" | "digest" | "settings";

const TAB_ITEMS = [
  { key: "trends", label: "Trends" },
  { key: "sources", label: "Sources" },
  { key: "hooks", label: "Brain hooks" },
  { key: "digest", label: "Digest" },
  { key: "settings", label: "Settings" },
];

interface TrendSourceDef {
  /** Maps to a TrendSourceKind (or "instagram" which isn't connectable yet). */
  key: TrendSourceKind;
  label: string;
  /** How this tenant authenticates this source (no creds are hardcoded). */
  auth: string;
}

// The four sources this surface presents in the catalog. reddit / youtube /
// custom are connectable today; instagram is shown but disabled (its adapter
// is a pending follow-up — see ConnectSourceSheet + FEATURE_CATALOG "Trends").
const TREND_SOURCES: TrendSourceDef[] = [
  { key: "instagram", label: "Instagram", auth: "Official Graph API Hashtag Search (planned)" },
  { key: "youtube", label: "YouTube", auth: "YouTube Data API key (your own)" },
  { key: "reddit", label: "Reddit", auth: "Public subreddit feeds (no key)" },
  { key: "custom", label: "Custom", auth: "RSS feed or URL you provide" },
];

// Generation defaults: a single click should produce something useful.
const GEN_PLATFORMS: Platform[] = [...PLATFORMS];
const GEN_KINDS: { key: ContentKind; label: string }[] = [
  { key: "caption", label: "Caption" },
  { key: "carousel", label: "Carousel" },
  { key: "script", label: "Script" },
  { key: "hook_options", label: "Hook options" },
];
const GEN_PROVIDERS: DraftProvider[] = ["bedrock"];

/** Pull a human-readable message off whatever the mutation/query threw. */
function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return "Something went wrong. Please try again.";
}

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function Trends() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const [tab, setTab] = useState<TabKey>("trends");
  const [connectOpen, setConnectOpen] = useState(false);

  // Scan-now: fire the rescan mutation, then poll the returned job.
  const rescan = useRescanTrends(ws, brand);
  const [scanJobId, setScanJobId] = useState<string | undefined>(undefined);
  const scanJob = useTrendScanJob(ws, brand, scanJobId);

  const sourcesQuery = useTrendSources(ws, brand);
  const clustersQuery = useTrendClusters(ws, brand);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/trends`} replace />;
  }

  const b = brandQuery.data?.brand;

  const scanStatus = scanJob.data?.job.status;
  const scanning = rescan.isPending || scanStatus === "queued" || scanStatus === "running";
  const scanError =
    (rescan.error ? errMessage(rescan.error) : null) ??
    (scanStatus === "failed" ? scanJob.data?.job.error || "The scan failed." : null);

  function runScan() {
    rescan.mutate(
      {},
      {
        onSuccess: ({ job }) => setScanJobId(job.jobId),
      },
    );
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn
          href={`/w/${ws}/b/${brand}`}
          variant="ghost"
          size="sm"
          iconLeft={<Icon name="arrow-left" size={13} />}
        >
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="arrow-up-right" size={20} /> Trends{b ? ` · ${b.name}` : ""}
          </h1>
          <div className={styles.headActions}>
            <Btn
              variant="secondary"
              onClick={runScan}
              disabled={scanning}
              loading={scanning}
              iconLeft={<Icon name="refresh" size={14} />}
            >
              {scanning ? "Scanning…" : "Scan now"}
            </Btn>
            <Btn variant="primary" onClick={() => setConnectOpen(true)} iconLeft={<Icon name="zap" size={14} />}>
              Connect source
            </Btn>
          </div>
        </header>

        {scanError && (
          <div className={styles.scanError} role="alert">
            <Icon name="alert" size={15} />
            <span>{scanError}</span>
          </div>
        )}

        <Hero clustersQuery={clustersQuery} sourcesQuery={sourcesQuery} />

        <div className={styles.tabsBar}>
          <Tabs items={TAB_ITEMS} value={tab} onChange={(k) => setTab(k as TabKey)} />
        </div>

        {tab === "trends" && <TrendsTab ws={ws} brand={brand} clustersQuery={clustersQuery} />}
        {tab === "sources" && (
          <SourcesTab
            ws={ws}
            brand={brand}
            sourcesQuery={sourcesQuery}
            onConnect={() => setConnectOpen(true)}
          />
        )}
        {tab === "hooks" && <BrainHooksTab ws={ws} brand={brand} />}
        {tab === "digest" && <DigestTab ws={ws} brand={brand} />}
        {tab === "settings" && <SettingsTab ws={ws} brand={brand} />}
      </div>

      <ConnectSourceSheet
        ws={ws}
        brand={brand}
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
      />
    </AppShell>
  );
}

/** Hero stat strip — real counts from the clusters + sources queries. */
function Hero({
  clustersQuery,
  sourcesQuery,
}: {
  clustersQuery: ReturnType<typeof useTrendClusters>;
  sourcesQuery: ReturnType<typeof useTrendSources>;
}) {
  const clusters = clustersQuery.data?.clusters;
  const sources = sourcesQuery.data?.sources;

  const dash = "—";
  const newCount = clusters ? String(clusters.filter((c) => c.status === "new").length) : dash;
  // "Watching" = hooks that live on the brain. Keyed on pushedToBrainAt (the
  // persistent fact) not status, so a trend stays counted after it's also
  // been used for generation (which flips status to "used").
  const watchingCount = clusters
    ? String(clusters.filter((c) => !!c.pushedToBrainAt).length)
    : dash;
  const sourcesCount = sources ? String(sources.length) : dash;

  return (
    <section className={styles.hero}>
      <p className={styles.heroLede}>
        Market-trends scan across this brand’s connected sources. The brain
        surfaces ranked trend clusters here and can push them into the brand
        brain or feed them into draft generation.
      </p>
      <div className={styles.statRow}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{newCount}</span>
          <span className={styles.statLabel}>New trends</span>
          <span className={styles.statHint}>not yet pushed or used</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{watchingCount}</span>
          <span className={styles.statLabel}>Watching</span>
          <span className={styles.statHint}>pushed to the brain</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{sourcesCount}</span>
          <span className={styles.statLabel}>Sources scanned</span>
          <span className={styles.statHint}>connected sources</span>
        </div>
        <div className={styles.stat}>
          {/* SCAFFOLD: no usage/quota backing data yet — see FEATURE_CATALOG Trends gaps */}
          <span className={styles.statValue}>—</span>
          <span className={styles.statLabel}>Quota used</span>
          <span className={styles.statHint}>across connected sources</span>
        </div>
      </div>
    </section>
  );
}

/** A small momentum chip. */
function MomentumChip({ momentum }: { momentum?: TrendClusterRecord["momentum"] }) {
  if (!momentum) return null;
  return (
    <span className={`${styles.momentum} ${styles[`m_${momentum}`] ?? ""}`}>{momentum}</span>
  );
}

/** Trends tab — Linear-style split: ranked list (left) + detail (right). */
function TrendsTab({
  ws,
  brand,
  clustersQuery,
}: {
  ws: string | undefined;
  brand: string | undefined;
  clustersQuery: ReturnType<typeof useTrendClusters>;
}) {
  const clusters = clustersQuery.data?.clusters ?? [];
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Default to the first (highest-ranked) cluster when the list loads, and
  // keep the selection valid if the list changes under us.
  useEffect(() => {
    const first = clusters[0];
    if (!first) {
      if (selectedId !== undefined) setSelectedId(undefined);
      return;
    }
    if (!selectedId || !clusters.some((c) => c.id === selectedId)) {
      setSelectedId(first.id);
    }
  }, [clusters, selectedId]);

  return (
    <div className={styles.split}>
      <div className={styles.listPane}>
        <div className={styles.paneHead}>Ranked trends</div>
        {clustersQuery.isLoading ? (
          <div className={styles.emptyPane}>
            <p className={styles.muted}>Loading trends…</p>
          </div>
        ) : clustersQuery.isError ? (
          <div className={styles.emptyPane}>
            <Icon name="alert" size={22} />
            <p>Couldn’t load trends.</p>
            <p className={styles.emptyHint}>{errMessage(clustersQuery.error)}</p>
          </div>
        ) : clusters.length === 0 ? (
          <div className={styles.emptyPane}>
            <Icon name="arrow-up-right" size={22} />
            <p>No trends yet.</p>
            <p className={styles.emptyHint}>
              Connect a source and run a scan. Detected topics will rank here
              with their relevance, source mix, and brand-match.
            </p>
          </div>
        ) : (
          <ul className={styles.clusterList}>
            {clusters.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`${styles.clusterRow} ${c.id === selectedId ? styles.clusterRowActive : ""}`}
                  onClick={() => setSelectedId(c.id)}
                >
                  <span className={styles.clusterTitle}>{c.title}</span>
                  <span className={styles.clusterMeta}>
                    <span className={styles.matchScore}>{Math.round(c.relevanceScore)}% match</span>
                    <MomentumChip momentum={c.momentum} />
                    <span className={styles.sourceMix}>
                      {c.contributions.length}{" "}
                      {c.contributions.length === 1 ? "source" : "sources"}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ClusterDetail ws={ws} brand={brand} clusterId={selectedId} />
    </div>
  );
}

/** Detail pane for the selected cluster. */
function ClusterDetail({
  ws,
  brand,
  clusterId,
}: {
  ws: string | undefined;
  brand: string | undefined;
  clusterId: string | undefined;
}) {
  const detail = useTrendCluster(ws, brand, clusterId);
  const pushToBrain = usePushClusterToBrain(ws, brand);
  const generate = useGenerateFromCluster(ws, brand);

  // Generate controls — defaults preselected so a one-click generate works.
  const [genPlatforms, setGenPlatforms] = useState<Platform[]>(["instagram"]);
  const [genKinds, setGenKinds] = useState<ContentKind[]>(["caption"]);
  const [genJobId, setGenJobId] = useState<string | null>(null);

  // Reset transient generate state when the selected cluster changes.
  useEffect(() => {
    setGenJobId(null);
    generate.reset();
    pushToBrain.reset();
    setGenPlatforms(["instagram"]);
    setGenKinds(["caption"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId]);

  if (!clusterId) {
    return (
      <div className={styles.detailPane}>
        <div className={styles.paneHead}>Trend detail</div>
        <div className={styles.emptyPane}>
          <Icon name="arrow-up-right" size={22} />
          <p>Select a trend.</p>
          <p className={styles.emptyHint}>Pick a trend from the list to see its detail.</p>
        </div>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className={styles.detailPane}>
        <div className={styles.paneHead}>Trend detail</div>
        <div className={styles.emptyPane}>
          <p className={styles.muted}>Loading detail…</p>
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.detailPane}>
        <div className={styles.paneHead}>Trend detail</div>
        <div className={styles.emptyPane}>
          <Icon name="alert" size={22} />
          <p>Couldn’t load this trend.</p>
          <p className={styles.emptyHint}>
            {detail.error ? errMessage(detail.error) : "Please try again."}
          </p>
        </div>
      </div>
    );
  }

  const { cluster, members } = detail.data;
  const isPushed = cluster.status === "pushed" || !!cluster.pushedToBrainAt;

  function togglePlatform(p: Platform) {
    setGenPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }
  function toggleKind(k: ContentKind) {
    setGenKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  const canGenerate = genPlatforms.length > 0 && genKinds.length > 0 && !generate.isPending;

  function runGenerate() {
    generate.mutate(
      {
        clusterId: cluster.id,
        platforms: genPlatforms,
        kinds: genKinds,
        providers: GEN_PROVIDERS,
      },
      {
        onSuccess: ({ jobId }) => setGenJobId(jobId),
      },
    );
  }

  return (
    <div className={styles.detailPane}>
      <div className={styles.paneHead}>{cluster.title}</div>

      <div className={styles.scoreRow}>
        <div className={styles.scoreCard}>
          <span className={styles.scoreValue}>{Math.round(cluster.relevanceScore)}%</span>
          <span className={styles.scoreLabel}>Relevance</span>
        </div>
        <div className={styles.scoreCard}>
          <span className={styles.scoreValue}>{Math.round(cluster.confidence)}%</span>
          <span className={styles.scoreLabel}>Confidence</span>
        </div>
        <div className={styles.scoreCard}>
          <span className={styles.scoreValue}>{cluster.momentum ?? "—"}</span>
          <span className={styles.scoreLabel}>Momentum</span>
        </div>
      </div>

      {(cluster.summary || cluster.relevanceReason) && (
        <div className={styles.detailBlock}>
          {cluster.summary && <p className={styles.muted}>{cluster.summary}</p>}
          {cluster.relevanceReason && (
            <p className={styles.relevanceReason}>{cluster.relevanceReason}</p>
          )}
        </div>
      )}

      <div className={styles.detailBlock}>
        <div className={styles.blockLabel}>Source breakdown</div>
        {cluster.peakTime && (
          <p className={styles.muted}>Peak window: {formatTime(cluster.peakTime)}</p>
        )}
        <SourceBreakdown contributions={cluster.contributions} members={members} />
      </div>

      <div className={styles.detailBlock}>
        <div className={styles.blockLabel}>Suggested draft</div>
        {cluster.suggestedHook ? (
          <p className={styles.suggestedHook}>“{cluster.suggestedHook}”</p>
        ) : (
          <p className={styles.muted}>No suggested hook for this trend.</p>
        )}

        <div className={styles.blockActions}>
          <Btn
            variant="secondary"
            size="sm"
            disabled={isPushed || pushToBrain.isPending}
            loading={pushToBrain.isPending}
            onClick={() => pushToBrain.mutate({ clusterId: cluster.id })}
          >
            {isPushed ? "Pushed" : pushToBrain.isPending ? "Pushing…" : "Push to brain"}
          </Btn>
        </div>
        {pushToBrain.isError && (
          <p className={styles.fieldError}>{errMessage(pushToBrain.error)}</p>
        )}
        {isPushed && cluster.pushedToBrainAt && (
          <p className={styles.muted}>Pushed to brain {formatTime(cluster.pushedToBrainAt)}.</p>
        )}

        {/* Generate-from-trend controls. */}
        <div className={styles.genControls}>
          <div className={styles.genGroup}>
            <span className={styles.genGroupLabel}>Platforms</span>
            <div className={styles.toggleRow}>
              {GEN_PLATFORMS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`${styles.toggle} ${genPlatforms.includes(p) ? styles.toggleOn : ""}`}
                  onClick={() => togglePlatform(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.genGroup}>
            <span className={styles.genGroupLabel}>Content</span>
            <div className={styles.toggleRow}>
              {GEN_KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  className={`${styles.toggle} ${genKinds.includes(k.key) ? styles.toggleOn : ""}`}
                  onClick={() => toggleKind(k.key)}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <p className={styles.genHint}>Generated with Bedrock.</p>
          <Btn
            variant="primary"
            size="sm"
            disabled={!canGenerate}
            loading={generate.isPending}
            iconLeft={<Icon name="zap" size={13} />}
            onClick={runGenerate}
          >
            {generate.isPending ? "Generating…" : "Generate from this trend"}
          </Btn>
        </div>
        {generate.isError && <p className={styles.fieldError}>{errMessage(generate.error)}</p>}
        {genJobId && (
          <p className={styles.genSuccess}>
            <Icon name="check-circle" size={14} /> Generation started (job {genJobId}).{" "}
            <a className={styles.genLink} href={`/w/${ws}/b/${brand}/queue`}>
              View in queue →
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

/** Per-source contributions + resolved member items for a cluster. */
function SourceBreakdown({
  contributions,
  members,
}: {
  contributions: TrendContribution[];
  members: TrendTopicRecord[];
}) {
  if (contributions.length === 0 && members.length === 0) {
    return <p className={styles.muted}>No source detail for this trend.</p>;
  }
  return (
    <div className={styles.breakdown}>
      {contributions.length > 0 && (
        <ul className={styles.contribList}>
          {contributions.map((c, i) => (
            <li key={`${c.sourceKind}-${c.sourceId}-${i}`} className={styles.contribRow}>
              <span className={`${styles.sourceDot} ${styles[`dot_${c.sourceKind}`] ?? ""}`} aria-hidden="true" />
              <span className={styles.contribKind}>{c.sourceKind}</span>
              <span className={styles.contribCount}>
                {c.count} {c.count === 1 ? "item" : "items"}
              </span>
              {c.sampleTitle && (
                <span className={styles.contribSample}>
                  {c.sampleUrl ? (
                    <a href={c.sampleUrl} target="_blank" rel="noopener noreferrer">
                      {c.sampleTitle}
                    </a>
                  ) : (
                    c.sampleTitle
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {members.length > 0 && (
        <ul className={styles.memberList}>
          {members.map((m) => (
            <li key={m.id} className={styles.memberRow}>
              <span className={`${styles.sourceDot} ${styles[`dot_${m.sourceKind}`] ?? ""}`} aria-hidden="true" />
              <span className={styles.memberTitle}>
                {m.url ? (
                  <a href={m.url} target="_blank" rel="noopener noreferrer">
                    {m.title}
                  </a>
                ) : (
                  m.title
                )}
              </span>
              <span className={styles.memberKind}>{m.sourceKind}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Sources tab — connected sources + the connectable catalog. */
function SourcesTab({
  ws,
  brand,
  sourcesQuery,
  onConnect,
}: {
  ws: string | undefined;
  brand: string | undefined;
  sourcesQuery: ReturnType<typeof useTrendSources>;
  onConnect: () => void;
}) {
  const disconnect = useDisconnectTrendSource(ws, brand);
  const sources = sourcesQuery.data?.sources ?? [];

  // Map kind -> connected sources, for the catalog connection state.
  const byKind = useMemo(() => {
    const m = new Map<TrendSourceKind, TrendSourceAccountRecord[]>();
    for (const s of sources) {
      const arr = m.get(s.kind) ?? [];
      arr.push(s);
      m.set(s.kind, arr);
    }
    return m;
  }, [sources]);

  return (
    <div className={styles.sourcesWrap}>
      <p className={styles.sourcesNote}>
        These are <strong>trend-scan read connections</strong> — they pull
        trending data only. They’re separate from the publish-side
        connections on <strong>Social accounts</strong>, which post out. No
        credentials are hardcoded; each workspace connects its own, stored in
        AWS SSM SecureString.
      </p>

      {/* Connected sources */}
      <div className={styles.sectionLabel}>Connected sources</div>
      {sourcesQuery.isLoading ? (
        <p className={styles.muted}>Loading sources…</p>
      ) : sourcesQuery.isError ? (
        <div className={styles.emptyPane}>
          <Icon name="alert" size={22} />
          <p>Couldn’t load sources.</p>
          <p className={styles.emptyHint}>{errMessage(sourcesQuery.error)}</p>
        </div>
      ) : sources.length === 0 ? (
        <div className={styles.emptyPane}>
          <Icon name="zap" size={22} />
          <p>No sources connected yet.</p>
          <p className={styles.emptyHint}>
            Connect a source below to start scanning for trends.
          </p>
        </div>
      ) : (
        <ul className={styles.connectedList}>
          {sources.map((s) => (
            <li key={s.sourceId} className={styles.connectedCard}>
              <div className={styles.sourceTop}>
                <span className={`${styles.sourceDot} ${styles[`dot_${s.kind}`] ?? ""}`} aria-hidden="true" />
                <span className={styles.sourceName}>{s.label}</span>
                <span className={`${styles.statusPill} ${styles[`status_${s.status}`] ?? ""}`}>
                  {s.status}
                </span>
              </div>
              <p className={styles.sourceAuth}>{s.kind}</p>
              {s.status === "error" && s.lastError && (
                <p className={styles.fieldError}>{s.lastError}</p>
              )}
              <dl className={styles.sourceMeta}>
                <div>
                  <dt>Last scan</dt>
                  <dd>{formatTime(s.lastScanAt)}</dd>
                </div>
                <div>
                  <dt>Items found</dt>
                  <dd>{s.lastScanItemCount ?? "—"}</dd>
                </div>
              </dl>
              <Btn
                variant="secondary"
                size="sm"
                full
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate({ sourceId: s.sourceId })}
              >
                Disconnect
              </Btn>
            </li>
          ))}
        </ul>
      )}
      {disconnect.isError && <p className={styles.fieldError}>{errMessage(disconnect.error)}</p>}

      {/* Connectable catalog */}
      <div className={styles.sectionLabel}>Available to connect</div>
      <ul className={styles.sourceGrid}>
        {TREND_SOURCES.map((s) => {
          const connected = byKind.get(s.key) ?? [];
          const unavailable = s.key === "instagram";
          return (
            <li key={s.key} className={styles.sourceCard}>
              <div className={styles.sourceTop}>
                <span className={`${styles.sourceDot} ${styles[`dot_${s.key}`] ?? ""}`} aria-hidden="true" />
                <span className={styles.sourceName}>{s.label}</span>
                {unavailable ? (
                  <span className={styles.notConnected}>Not available</span>
                ) : connected.length > 0 ? (
                  <span className={`${styles.statusPill} ${styles.status_active}`}>
                    {connected.length} connected
                  </span>
                ) : (
                  <span className={styles.notConnected}>Not connected</span>
                )}
              </div>
              <p className={styles.sourceAuth}>{s.auth}</p>
              {unavailable ? (
                <p className={styles.sourceAuth}>
                  Instagram trend scanning isn’t available yet — it needs the
                  official Graph API Hashtag Search integration (planned).
                </p>
              ) : null}
              <Btn variant="secondary" size="sm" full disabled={unavailable} onClick={onConnect}>
                {unavailable ? "Unavailable" : "Connect"}
              </Btn>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Brain hooks tab — clusters whose hook is on the brand brain. */
function BrainHooksTab({ ws, brand }: { ws: string | undefined; brand: string | undefined }) {
  // Filter on pushedToBrainAt (the persistent fact), not status === "pushed":
  // a trend you push to the brain and later generate from has status "used"
  // but its hook is still on the brain, so it belongs here.
  const pushed = useTrendClusters(ws, brand);
  const clusters = (pushed.data?.clusters ?? []).filter((c) => !!c.pushedToBrainAt);

  if (pushed.isLoading) {
    return <p className={styles.muted}>Loading brain hooks…</p>;
  }
  if (pushed.isError) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="alert" size={22} />
        <p>Couldn’t load brain hooks.</p>
        <p className={styles.emptyHint}>{errMessage(pushed.error)}</p>
      </div>
    );
  }
  if (clusters.length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="zap" size={22} />
        <p>No brain hooks yet.</p>
        <p className={styles.emptyHint}>
          Trend hooks you push to the brain will land here and be stored as
          seasonal windows on the brand brain, informing future drafts.
        </p>
      </div>
    );
  }

  return (
    <ul className={styles.hookList}>
      {clusters.map((c) => (
        <li key={c.id} className={styles.hookCard}>
          <div className={styles.hookTop}>
            <span className={styles.hookTitle}>{c.title}</span>
            <span className={styles.matchScore}>{Math.round(c.relevanceScore)}% match</span>
          </div>
          {c.suggestedHook ? (
            <p className={styles.suggestedHook}>“{c.suggestedHook}”</p>
          ) : (
            <p className={styles.muted}>No suggested hook stored for this trend.</p>
          )}
          <p className={styles.hookMeta}>Pushed {formatTime(c.pushedToBrainAt)}</p>
        </li>
      ))}
    </ul>
  );
}

/** Digest tab — Linear-style split: list of daily digests (left) + the
 *  selected digest's new-cluster detail (right). Opening an unread digest
 *  marks it read, which clears the nav badge. */
function DigestTab({ ws, brand }: { ws: string | undefined; brand: string | undefined }) {
  const digestsQuery = useTrendDigests(ws, brand);
  const markRead = useMarkDigestRead(ws, brand);

  // Server returns most-recent-first, but re-sort defensively.
  const digests = useMemo(() => {
    const list = [...(digestsQuery.data?.digests ?? [])];
    list.sort((a, b) => (a.generatedAt < b.generatedAt ? 1 : a.generatedAt > b.generatedAt ? -1 : 0));
    return list;
  }, [digestsQuery.data?.digests]);

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Default-select the most recent digest, and keep selection valid as the
  // list changes (e.g. after a new digest lands or the list invalidates).
  useEffect(() => {
    const first = digests[0];
    if (!first) {
      if (selectedId !== undefined) setSelectedId(undefined);
      return;
    }
    if (!selectedId || !digests.some((d) => d.id === selectedId)) {
      setSelectedId(first.id);
    }
  }, [digests, selectedId]);

  const selected = digests.find((d) => d.id === selectedId);

  // Mark the selected digest read exactly once when it's opened unread. Keyed
  // on the selected id so it fires on selection change, not on every render,
  // and guarded on !readAt + not-already-pending so it can't loop.
  useEffect(() => {
    if (!selected || selected.readAt) return;
    markRead.mutate({ digestId: selected.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <div className={styles.split}>
      <div className={styles.listPane}>
        <div className={styles.paneHead}>Daily digests</div>
        {digestsQuery.isLoading ? (
          <div className={styles.emptyPane}>
            <p className={styles.muted}>Loading digests…</p>
          </div>
        ) : digestsQuery.isError ? (
          <div className={styles.emptyPane}>
            <Icon name="alert" size={22} />
            <p>Couldn’t load digests.</p>
            <p className={styles.emptyHint}>{errMessage(digestsQuery.error)}</p>
          </div>
        ) : digests.length === 0 ? (
          <div className={styles.emptyPane}>
            <Icon name="bell" size={22} />
            <p>No digests yet.</p>
            <p className={styles.emptyHint}>
              When daily scanning is on, a digest of new trend clusters lands
              here each day. Turn it on under <strong>Settings → Daily
              digest</strong>.
            </p>
          </div>
        ) : (
          <ul className={styles.clusterList}>
            {digests.map((d) => {
              const unread = !d.readAt;
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    className={`${styles.clusterRow} ${d.id === selectedId ? styles.clusterRowActive : ""}`}
                    onClick={() => setSelectedId(d.id)}
                  >
                    <span className={styles.digestRowTop}>
                      <span className={styles.clusterTitle}>{formatTime(d.generatedAt)}</span>
                      {unread && <span className={styles.unreadPill}>Unread</span>}
                    </span>
                    <span className={styles.clusterMeta}>
                      <span className={styles.sourceMix}>
                        {d.totalNewClusters} new {d.totalNewClusters === 1 ? "trend" : "trends"}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <DigestDetail digest={selected} />
    </div>
  );
}

/** Detail pane for the selected digest — its new-cluster summaries. */
function DigestDetail({ digest }: { digest: TrendDigestRecord | undefined }) {
  if (!digest) {
    return (
      <div className={styles.detailPane}>
        <div className={styles.paneHead}>Digest detail</div>
        <div className={styles.emptyPane}>
          <Icon name="bell" size={22} />
          <p>Select a digest.</p>
          <p className={styles.emptyHint}>Pick a digest from the list to see its new trends.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailPane}>
      <div className={styles.paneHead}>Digest · {formatTime(digest.generatedAt)}</div>

      <div className={styles.detailBlock}>
        <p className={styles.muted}>
          Trends discovered {formatTime(digest.periodStart)} – {formatTime(digest.periodEnd)}
        </p>
        {digest.clusters.length === 0 ? (
          <p className={styles.muted}>No new trends in this window.</p>
        ) : (
          <ul className={styles.digestClusterList}>
            {digest.clusters.map((c) => (
              <li key={c.clusterId} className={styles.digestClusterCard}>
                <div className={styles.digestClusterTop}>
                  <span className={styles.clusterTitle}>{c.title}</span>
                  <span className={styles.digestClusterMeta}>
                    <span className={styles.matchScore}>{Math.round(c.relevanceScore)}% match</span>
                    <MomentumChip momentum={c.momentum} />
                  </span>
                </div>
                {c.suggestedHook && (
                  <p className={styles.suggestedHook}>“{c.suggestedHook}”</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Settings tab — scan config. Loads + persists via the config endpoint. */
function SettingsTab({ ws, brand }: { ws: string | undefined; brand: string | undefined }) {
  const configQuery = useTrendConfig(ws, brand);
  const save = useUpdateTrendConfig(ws, brand);

  // Slack digest delivery: only owners/admins may configure it (server enforces
  // too; this is just UX). The webhook URL is a server-side secret — the client
  // only ever sees whether it's connected via `digestSlackWebhookSsmKey`.
  const wsQuery = useWorkspace(ws);
  const isAdmin = wsQuery.data?.role === "owner" || wsQuery.data?.role === "admin";
  const connectSlack = useConnectDigestSlack(ws, brand);
  const disconnectSlack = useDisconnectDigestSlack(ws, brand);

  const config = configQuery.data?.config;

  // Local form state, seeded from the loaded config.
  const [cadence, setCadence] = useState<TrendConfigRecord["cadence"]>("daily");
  const [minRelevance, setMinRelevance] = useState(50);
  const [autoPush, setAutoPush] = useState(false);
  const [watchlistText, setWatchlistText] = useState("");
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Slack connect form — its own state, NOT folded into the Save button.
  const [slackWebhookUrl, setSlackWebhookUrl] = useState("");
  const [slackClientError, setSlackClientError] = useState<string | null>(null);

  useEffect(() => {
    if (config && !seeded) {
      setCadence(config.cadence);
      setMinRelevance(config.minRelevance);
      setAutoPush(config.autoPushToBrain);
      setWatchlistText((config.watchlistKeywords ?? []).join(", "));
      setDigestEnabled(config.digestEnabled ?? false);
      setSeeded(true);
    }
  }, [config, seeded]);

  if (configQuery.isLoading) {
    return <p className={styles.muted}>Loading settings…</p>;
  }
  if (configQuery.isError || !config) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="alert" size={22} />
        <p>Couldn’t load settings.</p>
        <p className={styles.emptyHint}>
          {configQuery.error ? errMessage(configQuery.error) : "Please try again."}
        </p>
      </div>
    );
  }

  function parseWatchlist(text: string): string[] {
    return text
      .split(/[,\n]/)
      .flatMap((s) => s.split(/\s+/))
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function handleSave() {
    save.mutate({
      cadence,
      autoPushToBrain: autoPush,
      minRelevance,
      watchlistKeywords: parseWatchlist(watchlistText),
      digestEnabled,
    });
  }

  // Slack connect/disconnect are immediate, separate mutations (not part of the
  // Save button). The webhook URL never round-trips to the client — we only
  // submit it and read back the connected flag.
  const slackConnected = !!config.digestSlackWebhookSsmKey;

  function handleConnectSlack() {
    setSlackClientError(null);
    const url = slackWebhookUrl.trim();
    if (!url || !url.startsWith("https://hooks.slack.com/")) {
      setSlackClientError(
        "Enter a Slack Incoming Webhook URL starting with https://hooks.slack.com/.",
      );
      return;
    }
    connectSlack.mutate(
      { webhookUrl: url },
      {
        onSuccess: () => setSlackWebhookUrl(""),
      },
    );
  }

  const dirty =
    cadence !== config.cadence ||
    minRelevance !== config.minRelevance ||
    autoPush !== config.autoPushToBrain ||
    digestEnabled !== (config.digestEnabled ?? false) ||
    parseWatchlist(watchlistText).join(",") !== (config.watchlistKeywords ?? []).join(",");

  return (
    <div className={styles.settings}>
      <SettingRow label="Scan cadence" hint="How often the scanner runs. Off disables scanning.">
        <Segmented
          items={[
            { key: "off", label: "Off" },
            { key: "hourly", label: "Hourly" },
            { key: "daily", label: "Daily" },
            { key: "weekly", label: "Weekly" },
          ]}
          value={cadence}
          onChange={(k) => setCadence(k as TrendConfigRecord["cadence"])}
          size="sm"
        />
      </SettingRow>

      <SettingRow
        label="Relevance threshold"
        hint="Minimum brand-relevance (0–100) to surface a trend."
      >
        <div className={styles.rangeControl}>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={minRelevance}
            onChange={(e) => setMinRelevance(Number(e.target.value))}
            className={styles.range}
            aria-label="Relevance threshold"
          />
          <span className={styles.rangeValue}>{minRelevance}</span>
        </div>
      </SettingRow>

      <SettingRow
        label="Auto-push to brain"
        hint="Auto-add trends scoring at or above the relevance threshold to the brand brain."
      >
        <Checkbox
          checked={autoPush}
          onChange={(e) => setAutoPush(e.target.checked)}
          label={autoPush ? "On" : "Off"}
        />
      </SettingRow>

      <SettingRow label="Watchlist keywords" hint="Terms to bias the scan toward (comma or space separated).">
        <Input
          value={watchlistText}
          onChange={(e) => setWatchlistText(e.target.value)}
          placeholder="e.g. skincare, retinol, SPF"
        />
      </SettingRow>

      <SettingRow
        label="Daily digest"
        hint="Build an in-app daily digest of newly-discovered trends. View it in the Digest tab."
      >
        <Checkbox
          checked={digestEnabled}
          onChange={(e) => setDigestEnabled(e.target.checked)}
          label={digestEnabled ? "On" : "Off"}
        />
      </SettingRow>

      <SettingRow
        label="Slack delivery"
        hint="Also post each daily digest to a Slack channel via an Incoming Webhook. Workspace admins only."
      >
        <div className={styles.slackControl}>
          {!isAdmin ? (
            // Non-admin: read-only status only. The server rejects mutations
            // from non-admins, so we don't render the input or buttons.
            <>
              <span className={styles.muted}>
                {slackConnected ? "Slack connected" : "Not connected"}
              </span>
              <span className={styles.muted}>Only a workspace admin can change this.</span>
            </>
          ) : slackConnected ? (
            // Admin + connected: status + Disconnect.
            <>
              <div className={styles.slackStatusRow}>
                <span className={styles.muted}>Slack connected</span>
                <Btn
                  variant="secondary"
                  size="sm"
                  disabled={disconnectSlack.isPending}
                  loading={disconnectSlack.isPending}
                  onClick={() => disconnectSlack.mutate()}
                >
                  {disconnectSlack.isPending ? "Disconnecting…" : "Disconnect"}
                </Btn>
              </div>
              {disconnectSlack.isError && (
                <span className={styles.fieldError}>{errMessage(disconnectSlack.error)}</span>
              )}
            </>
          ) : (
            // Admin + not connected: webhook input + Connect.
            <>
              <div className={styles.slackConnectForm}>
                <Input
                  value={slackWebhookUrl}
                  onChange={(e) => setSlackWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                  aria-label="Slack Incoming Webhook URL"
                />
                <Btn
                  variant="primary"
                  size="sm"
                  disabled={connectSlack.isPending}
                  loading={connectSlack.isPending}
                  onClick={handleConnectSlack}
                >
                  {connectSlack.isPending ? "Connecting…" : "Connect"}
                </Btn>
              </div>
              {slackClientError && <span className={styles.fieldError}>{slackClientError}</span>}
              {connectSlack.isError && (
                <span className={styles.fieldError}>{errMessage(connectSlack.error)}</span>
              )}
            </>
          )}
          {isAdmin && !digestEnabled && (slackConnected || connectSlack.isPending) && (
            <span className={styles.muted}>
              Turn on Daily digest above for Slack delivery to send.
            </span>
          )}
        </div>
      </SettingRow>

      <div className={styles.settingsFooter}>
        <Btn
          variant="primary"
          size="sm"
          disabled={!dirty || save.isPending}
          loading={save.isPending}
          onClick={handleSave}
        >
          {save.isPending ? "Saving…" : "Save settings"}
        </Btn>
        {save.isSuccess && !dirty && (
          <span className={styles.savedNote}>
            <Icon name="check-circle" size={14} /> Saved
          </span>
        )}
        {save.isError && <span className={styles.fieldError}>{errMessage(save.error)}</span>}
      </div>
    </div>
  );
}

function SettingRow({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div className={styles.settingRow}>
      <div className={styles.settingText}>
        <div className={styles.settingLabel}>{label}</div>
        <div className={styles.settingHint}>{hint}</div>
      </div>
      <div className={styles.settingControl}>{children}</div>
    </div>
  );
}

/** Kinds the user can pick when connecting a source (instagram excluded). */
type ConnectableKind = "reddit" | "youtube" | "custom";

/** Connect-source sheet — a real connect form, one panel per source kind. */
function ConnectSourceSheet({
  ws,
  brand,
  open,
  onClose,
}: {
  ws: string | undefined;
  brand: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const connect = useConnectTrendSource(ws, brand);
  const [kind, setKind] = useState<ConnectableKind>("reddit");

  // Per-kind inputs.
  const [subreddits, setSubreddits] = useState("");
  const [channelIds, setChannelIds] = useState("");
  const [keywords, setKeywords] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);

  // Reset the form whenever the sheet opens.
  useEffect(() => {
    if (open) {
      setKind("reddit");
      setSubreddits("");
      setChannelIds("");
      setKeywords("");
      setFeedUrl("");
      setClientError(null);
      connect.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function splitList(text: string): string[] {
    return text
      .split(/[,\n]/)
      .flatMap((s) => s.split(/\s+/))
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function handleSubmit() {
    setClientError(null);
    let cfg: TrendSourceConfig;
    let label: string;

    if (kind === "reddit") {
      const subs = splitList(subreddits).map((s) => s.replace(/^\/?r\//i, ""));
      if (subs.length === 0) {
        setClientError("Enter at least one subreddit.");
        return;
      }
      cfg = { kind: "reddit", subreddits: subs };
      label = subs.length === 1 ? `r/${subs[0]}` : `${subs.length} subreddits`;
    } else if (kind === "youtube") {
      const ch = splitList(channelIds);
      const kw = splitList(keywords);
      if (ch.length === 0 && kw.length === 0) {
        setClientError("Enter at least one channel ID or keyword.");
        return;
      }
      cfg = {
        kind: "youtube",
        ...(ch.length ? { channelIds: ch } : {}),
        ...(kw.length ? { keywords: kw } : {}),
      };
      label =
        ch.length && kw.length
          ? "YouTube channels + keywords"
          : ch.length
            ? `${ch.length} YouTube channel${ch.length === 1 ? "" : "s"}`
            : `YouTube: ${kw.join(", ")}`;
    } else {
      const url = feedUrl.trim();
      if (!/^https?:\/\//i.test(url)) {
        setClientError("Enter a feed URL starting with http:// or https://.");
        return;
      }
      cfg = { kind: "custom", feedUrl: url };
      label = url;
    }

    connect.mutate(
      { kind, config: cfg, label },
      {
        onSuccess: () => onClose(),
      },
    );
  }

  const kindTabs: { key: ConnectableKind; label: string }[] = [
    { key: "reddit", label: "Reddit" },
    { key: "youtube", label: "YouTube" },
    { key: "custom", label: "Custom" },
  ];

  return (
    <Modal
      open={open}
      title="Connect a trend source"
      onClose={onClose}
      footer={
        <>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={connect.isPending}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={connect.isPending}
            loading={connect.isPending}
          >
            {connect.isPending ? "Connecting…" : "Connect"}
          </Btn>
        </>
      }
    >
      <p className={styles.muted}>
        Each source stores any credential in AWS SSM SecureString under{" "}
        <code>/sm/&lt;stage&gt;/trends/&lt;source&gt;</code> — never in the
        table or the browser. Reddit, Google Trends, and custom feeds need no
        credential.
      </p>

      <div className={styles.connectTabs}>
        <Segmented
          items={kindTabs}
          value={kind}
          onChange={(k) => {
            setKind(k as ConnectableKind);
            setClientError(null);
            connect.reset();
          }}
          size="sm"
        />
      </div>

      {/* Instagram: shown but not connectable. Honest reflection of backend. */}
      <div className={`${styles.provider} ${styles.providerDisabled}`}>
        <span className={`${styles.sourceDot} ${styles.dot_instagram}`} aria-hidden="true" />
        <span className={styles.providerName}>Instagram</span>
        <span className={styles.providerAuth}>
          Not available yet — needs the official Graph API Hashtag Search
          integration (planned).
        </span>
      </div>

      <div className={styles.connectForm}>
        {kind === "reddit" && (
          <label className={styles.formField}>
            <span className={styles.formLabel}>Subreddits</span>
            <Input
              value={subreddits}
              onChange={(e) => setSubreddits(e.target.value)}
              placeholder="skincare, beauty (r/ optional)"
            />
            <span className={styles.formHint}>Comma or space separated. The “r/” prefix is optional.</span>
          </label>
        )}

        {kind === "youtube" && (
          <>
            <label className={styles.formField}>
              <span className={styles.formLabel}>Channel IDs</span>
              <Input
                value={channelIds}
                onChange={(e) => setChannelIds(e.target.value)}
                placeholder="UCxxxx, UCyyyy"
              />
            </label>
            <label className={styles.formField}>
              <span className={styles.formLabel}>Keywords</span>
              <Input
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="retinol, SPF"
              />
              <span className={styles.formHint}>Provide channel IDs and/or keywords (at least one).</span>
            </label>
          </>
        )}

        {kind === "custom" && (
          <label className={styles.formField}>
            <span className={styles.formLabel}>Feed URL</span>
            <Input
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="https://example.com/feed.xml"
            />
            <span className={styles.formHint}>RSS, Atom, or JSON feed URL (must start with http(s)).</span>
          </label>
        )}

        {clientError && <p className={styles.fieldError}>{clientError}</p>}
        {connect.isError && <p className={styles.fieldError}>{errMessage(connect.error)}</p>}
      </div>
    </Modal>
  );
}
